import * as React from 'react';
import Image from 'next/image';
import { DateSelect } from 'react-scale/components/pages/events/dropdown';
import { ViewSwitch } from 'react-scale/components/pages/events/switch';
import { Tag, TagsGroup } from 'react-scale/components/pages/events/tags';
import CTA from 'react-scale/components/elements/cta';
import clsx from 'clsx';
import gsap from 'gsap';
import { Navigation } from './navigation';
import { useRouter } from 'next/router';
import { BuilderEvent, Event, getEventDays } from './utils';

type EventsListProps = {
  events: { event?: BuilderEvent }[];
};

export const EventsList = (props: EventsListProps) => {
  const listRef = React.useRef<HTMLDivElement>(null);
  const [isGridView, setIsGridView] = React.useState(true);

  const [dateFilter, setDateFilter] = React.useState<'upcoming' | 'past'>('upcoming');
  const handleDateChange: React.ChangeEventHandler<HTMLSelectElement> = event => {
    setDateFilter(event.currentTarget.value as any);
  };

  const [currentPage, setCurrentPage] = React.useState(0);
  const { query } = useRouter();

  const filtersContainer = React.useRef<HTMLDivElement>(null);

  const [typesSelected, setTypesSelected] = React.useState<string[]>([]);
  const handleTypeChange = (label: string, value: boolean | 'indeterminate') => {
    if (value) setTypesSelected(prev => [...prev, label]);
    if (!value) setTypesSelected(prev => [...prev].filter(selected => label !== selected));
  };

  const filteredEvents = React.useMemo(() => {
    const filtered = props.events.filter(({ event }) => {
      if (!typesSelected.length || typesSelected.includes(event.value.data.type)) {
        const now = new Date();
        const eventDate = new Date(event.value.data.endDate);

        if (dateFilter === 'past') {
          return eventDate < now;
        } else {
          return eventDate >= now;
        }
      }

      return false;
    });
    return filtered;
  }, [props.events, dateFilter, typesSelected]);

  const [step, pages] = React.useMemo(() => {
    const step = isGridView ? 8 : 4;
    return [step, Math.ceil(filteredEvents.length / step)];
  }, [isGridView, filteredEvents]);

  React.useEffect(() => {
    Number(query.pageNumber) && setCurrentPage(Number(query.pageNumber) - 1);
    query.viewMode && setIsGridView(query.viewMode === 'grid' ? true : false);
    query.pastEvents && JSON.parse(query.pastEvents.toString()) && setDateFilter('past');
  }, []);

  React.useEffect(() => {
    if (!listRef.current) return;
    const tl = gsap.timeline();

    tl.set(listRef.current, { autoAlpha: 0 });
    tl.to(listRef.current, { autoAlpha: 1, duration: 1.2 });

    return () => {
      tl.kill();
    };
  }, [isGridView, dateFilter, currentPage, typesSelected]);

  const handleReset =
    (func: Function) =>
    (...params: any[]) => {
      setCurrentPage(0);
      func(...params);
    };

  const handlePageChange = (pageNumber: number) => {
    filtersContainer.current?.scrollIntoView({ behavior: 'smooth' });
    filtersContainer.current?.scrollIntoView();
    setCurrentPage(pageNumber);
  };

  return (
    <>
      <div
        ref={filtersContainer}
        className="flex flex-col lg:flex-row gap-4 w-full my-[68px] mt-10 lg:mb-[84px]"
      >
        <ViewSwitch checked={isGridView} onCheckedChange={handleReset(setIsGridView)} />
        <DateSelect onSelectChange={handleReset(handleDateChange)} value={dateFilter} />
        <TagsGroup
          selected={typesSelected}
          onTagChange={handleTypeChange}
          className="lg:ml-auto lg:mr-0 lg:px-0 lg:w-fit lg:max-w-1/2 w-screen -mx-5 px-5 overflow-x-auto"
        />
      </div>
      {filteredEvents.length ? (
        <section
          ref={listRef}
          className={clsx(
            'grid grid-cols-1 gap-x-[30px] gap-y-10 lg:gap-y-20',
            isGridView && 'lg:grid-cols-2'
          )}
        >
          {filteredEvents.slice(currentPage * step, currentPage * step + step).map(({ event }) => (
            <EventCard
              {...event.value.data}
              key={event.id}
              variant={isGridView ? 'grid' : 'list'}
            />
          ))}
        </section>
      ) : (
        <EmptyState ref={listRef} />
      )}
      <Navigation
        className="mt-7 lg:mt-[84px] lg:mb-[38px]"
        pages={pages}
        currentPage={currentPage}
        isInPage
        pageCallback={handlePageChange}
      />
    </>
  );
};

const EventCard = ({ variant = 'grid', ...props }: Event & { variant?: 'grid' | 'list' }) => {
  const eventDays = React.useCallback(getEventDays, []);

  const imgSrc = React.useMemo(
    () => (variant === 'grid' && props.gridCover ? props.gridCover : props.cover),
    [props.gridCover, props.cover, variant]
  );

  return (
    <article
      className={clsx(
        variant === 'grid'
          ? 'lg:grid-cols-[4fr_5fr] gap-x-11 p-11'
          : 'lg:h-[404px] lg:grid-cols-[5fr_4fr] gap-x-10 p-9',
        'rounded-[10px] grid gap-y-7 bg-[#0C0C0CB2] lg:p-[38px] lg:grid-rows-[repeat(2,auto)_1fr_repeat(2,auto)] border border-[#414141] overflow-hidden'
      )}
    >
      <Image
        width={1260}
        height={750}
        src={imgSrc}
        alt={`${props.title} Cover image`}
        quality={100}
        className="lg:row-span-full lg:max-h-none -mx-11 min-w-[calc(100%_+_88px)] w-full lg:min-w-[calc(100%_+_38px)] h-auto max-h-[207px] lg:h-[calc(100%_+_76px)] object-cover -mt-11 lg:-m-[38px] lg:mr-0"
      />
      <span className="text-xl font-aeonik text-[#BBDEF2]">
        {eventDays(props.startDate, props.endDate)}
      </span>
      <h4 className="text-2xl leading-[1]">{props.title}</h4>
      <span className="text-[#A3A3A3] leading-none">{props.location}</span>
      <Tag as="span" className={clsx('mt-[10px]', variant === 'grid' && 'lg:mt-[48px]')}>
        {props.type}
      </Tag>
      {/* @ts-expect-error Unreachable types */}
      <CTA
        cta={{
          buttons: [
            {
              text: props.ctaLabel,
              href: props.link,
              type: 'secondary',
              variant: 'underlined',
              target: props.link.startsWith('/') ? '_self' : '_blank'
            }
          ]
        }}
      />
    </article>
  );
};

const EmptyState = React.forwardRef<HTMLDivElement>((_, ref) => {
  return (
    <div
      ref={ref}
      className="flex flex-col gap-[18px] my-[240px] justify-center items-center mx-auto text-center"
    >
      <svg
        width="24"
        height="24"
        className="mb-[7px]"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
          stroke="#D4D4D4"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M16 16C16 16 14.5 14 12 14C9.5 14 8 16 8 16"
          stroke="#D4D4D4"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M9 9H9.01"
          stroke="#D4D4D4"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M15 9H15.01"
          stroke="#D4D4D4"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <h4 className="font-bold text-2xl max-w-[620px]">
        It seems we were unable to find events matching your current filter criteria.
      </h4>
      <p className="text-[#6C6C6C] max-w-[420px]">
        Please broaden your search or try removing some of the filters to see more options.
      </p>
    </div>
  );
});
